/* eslint-disable react/no-array-index-key */
/* eslint-disable jsx-a11y/label-has-associated-control */
import React, { useState, forwardRef, useImperativeHandle, Fragment } from 'react';
import PropTypes from 'prop-types';
import FormValidation from '../formEnums/formValidation';
import { AsyncTypeahead } from 'react-bootstrap-typeahead';
import Select from '../../atoms/select/select';
import 'react-bootstrap-typeahead/css/Typeahead.css';
import { composeURL } from '../../../utils/urlTools';
import { httpGet } from '../../../store/externalCalls/httpCalls';
import { useEffect } from 'react';
import './asyncSelect.scss';
import { shallowEqual, useSelector } from 'react-redux';
import { storeStructure } from '../../../store/reducers';

/**
 * Basic HTML select with an object array input parameter to bind to other react components
 */

const AsyncSelect = forwardRef((props, ref) => {
  const {
    validation, onChange, value, className, disabled, placeholder, session, url, filter, id, 
    multiple, fieldValue, fieldName, emptyValue, pageId, parentField, refreshData, allValue,
    writable, fieldDisabled, fieldDisabledReason, loadInitialWithoutParent,sessionParams, 
    noOptionsMessageText, customStyles, ignoreEmptyValue, fieldCode} = props;

  const [valueState, setValueState] = useState(value);
  const [isLoading, setIsLoading] = useState(false);
  const [refresh,setRefresh] =useState(null);
  const [options, setOptions] = useState([]);
  const storedSession = useSelector((state) => state[storeStructure.Session], shallowEqual);

  const handleSearch = (query) => {
    setIsLoading(true);
    httpGet(url+'?'+filter+'='+query).then(results =>{
      setOptions(results);
      setIsLoading(false);
    })
  };
  const handleRefreshSearch = (id) => {
    if (parseInt(id) > 0) {
      let paramId = "/" + id;
      // console.log("load info");
      httpGet(url + paramId).then(results => {
        let op = [];
        if (emptyValue)
          op.push({ "value": '(Empty)', "name": '(no selection)' });
        results.forEach(el => {
          op.push({ ...el, value: el[fieldValue], name: el[fieldName] });
        });
        if (allValue) {
          op.push({ value: allValue, name: allValue });
        }
        setOptions(op);
        setIsLoading(false);
      })
    } else {
      if (ignoreEmptyValue) {
        httpGet(url).then(results => {
          let op = [];
          if (emptyValue)
            op.push({ "value": '(Empty)', "name": '(no selection)' });
          results.forEach(el => {
            op.push({ ...el, value: el[fieldValue], name: el[fieldName] });
          });
          if (allValue) {
            op.push({ value: allValue, name: allValue });
          }
          setOptions(op);
          setIsLoading(false);
        })
      }
    }
  }
  const handleSimpleSearch = () => {
    let editId="";
    if(pageId){
      let location=window.location.pathname;
      let locationArr = location.split("/");
      editId="/"+locationArr[locationArr.length-1];
    }
    setIsLoading(true);
    let getUrl=url;
    if(session)
      getUrl=composeURL(storedSession, session, url, editId);
    if(sessionParams && sessionParams.length>0){
      getUrl=composeURL(storedSession, sessionParams, url, editId);
    }
    httpGet(getUrl+editId).then(results =>{
      let op=[];
      if(emptyValue)
        op.push({"value":'(Empty)',"name":'(no selection)'});
      results.forEach(el=>{
        op.push({...el,value:el[fieldValue],name:el[fieldName],disabled:(el[fieldDisabled]==1?true:false),disabledReason:el[fieldDisabledReason]});
      });
      setOptions(op);
      setIsLoading(false);
    })
  };
  const handleSimpleChange = (e)=>{
    const selectedValue = e?.target?.value;
    setValueState(selectedValue);
    let payload ={
      ...e,
      target: {
        ...e.target,
        value: selectedValue, 
        option: e?.target?.option ?? null
      },
    };

    onChange(payload);
  }

  useImperativeHandle(ref, () => ({
    updateValue(newValue) {
      setValueState(newValue);
    },
  }));

  useEffect(() => {
    if(!multiple){
      if(parentField){
        if(loadInitialWithoutParent){
          handleSimpleSearch();
        }
        //no
      }else
        handleSimpleSearch();
    }
  }, []);
   
  useEffect(() => {
    handleRefreshSearch(refreshData);
  }, [refreshData]);
   
  useEffect(() => {
    if(!multiple){
      if(value){
        setValueState(value);
      }
    }
  }, [value]);

  const filterBy = () => true;
    return (
      <>
      {multiple && <AsyncTypeahead
        className={`asyncSelect`}
        clearButton
        multiple
        onChange={(selected) => {
          onChange(selected);
        }}
        filterBy={filterBy}
        id={id}
        isLoading={isLoading}
        labelKey="code"
        minLength={1}
        selected={value}
        onSearch={handleSearch}
        options={options}
        placeholder={placeholder}
        renderMenuItemChildren={(option, props) => (
          <Fragment>
            <span>{( option[fieldName] ?? option.name)}</span> - <span>{option[fieldCode] ?? option.code}</span>
          </Fragment>
        )}
      />}
      {!multiple && <Select
        id={id}
        options={options}
        onChange={handleSimpleChange}
        value={valueState}
        disabled={disabled}
        writable={writable}
        noOptionsMessageText={noOptionsMessageText}
        customStyles={customStyles}
        />}
    </>
    )
});

AsyncSelect.propTypes = {
  /**
   * Array with object(name, value). It defines the select options
   */
  options: PropTypes.arrayOf(PropTypes.shape({
    name: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.bool]),
  })).isRequired,
  /**
   * Input form status, it is a FormValidation enum
   */
  validation: PropTypes.oneOf(Object.values(FormValidation)),
  /**
   * Selected value
   */
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.bool, PropTypes.array]),
  /**
   * Function to bind onChange HTML functionality
   */
  onChange: PropTypes.func,
  /**
   * Custom CSS classes
   */
  className: PropTypes.string,
  /**
   * To disable the select functionality
   */
  disabled: PropTypes.bool,
  noOptionsMessageText: PropTypes.string,
};

AsyncSelect.defaultProps = {
  value: '',
  validation: FormValidation.PENDING,
  onChange: () => {},
  className: '',
  disabled: false,
  noOptionsMessageText: undefined,
  customStyles: null,
  ignoreEmptyValue: false,
  fieldCode: 'code'
};

export default AsyncSelect;
